import { useState } from 'react';
import clsx from 'clsx';
import { Wand2, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { settingsService } from '@/services/settings.service';

interface Props {
  /** Current textarea/input value to enhance. */
  value: string;
  /** Called with the enhanced text when the AI returns. */
  onChange: (next: string) => void;
  /**
   * Optional hint about where this prompt will be used (e.g. "WhatsApp bot
   * flow brief", "Ad creative caption"). Helps the model tailor the rewrite.
   */
  context?: string;
  /** Disable when the parent form is busy. */
  disabled?: boolean;
  /** Compact variant — used inline next to action buttons. */
  size?: 'sm' | 'md';
  className?: string;
  /** Override label text (defaults to "Enhance"). */
  label?: string;
  /** Optional model override — pass these when the button sits next to a
      model picker so Enhance uses the SAME model the user chose there. */
  provider?: string;
  model?: string;
}

/**
 * Reusable button that polishes the user's prompt via the company's DEFAULT
 * AI model (configured on /settings/ai). Drop it next to any prompt textarea.
 *
 * Generation features (auto-generate bot flow, template AI assist, etc.) still
 * let the user pick a model on the same page — only ENHANCEMENT uses the
 * default, so it stays consistent across the app and is governed centrally.
 */
export function EnhancePromptButton({
  value, onChange, context, disabled, size = 'sm', className, label = 'Enhance', provider, model,
}: Props) {
  const [busy, setBusy] = useState(false);

  const onClick = async () => {
    const text = (value ?? '').trim();
    if (!text) {
      toast.error('Write a prompt first, then I\'ll enhance it.');
      return;
    }
    setBusy(true);
    try {
      const r = await settingsService.enhancePrompt({ prompt: text, context, provider, model });
      if (!r.enhanced) {
        toast.error('Enhancement returned an empty result.');
        return;
      }
      onChange(r.enhanced);
      toast.success('Prompt enhanced.');
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      toast.error(err?.response?.data?.message ?? 'Enhance failed. Set a default AI model in /settings/ai.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || busy}
      title="Enhance this prompt with AI (uses your default model)"
      className={clsx(
        'inline-flex items-center gap-1.5 rounded-lg border border-primary/30 bg-primary-soft text-primary',
        'transition-colors hover:bg-primary/15 hover:border-primary/60 disabled:cursor-not-allowed disabled:opacity-60',
        size === 'sm' ? 'h-8 px-2.5 text-xs font-medium' : 'h-10 px-3 text-sm font-medium',
        className,
      )}
    >
      {busy ? <Loader2 size={size === 'sm' ? 13 : 15} className="animate-spin" /> : <Wand2 size={size === 'sm' ? 13 : 15} />}
      {label}
    </button>
  );
}
